import _isEmpty from 'lodash/isEmpty';

import { Link, useLocation } from '@reach/router';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CommonLayoutV2, CustomButtonV2, CustomInputV2, CustomSnackbarV2 } from 'components/v2';

import { axios } from 'services/axios';
import { COMMON_ERROR_MESSAGE, errorMessageMap, ERRORS_CODE, NETWORK_ERROR_MESSAGE } from 'services/Constant';
import { logService } from 'services/LogService';
import QueryParametersParser from 'services/QueryParametersParser';
import { getAppNameByID } from 'services/utility';
import Validator from 'services/Validator';

import './reset-password.scss';

const pageTitle = 'OPSWAT Accounts | Reset Password';

const ResetPasswordPageV2 = ({ location }) => {
    const { search } = useLocation();

    const [email, setEmail] = useState(location?.state?.email ?? '');
    const [autoSubmit] = useState(location?.state?.autoSubmit ?? false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [requestError, setRequestError] = useState([]);

    const isValid = useMemo(() => Validator.isEmail(email), [email]);

    const errors = useMemo(() => (!isValid && email ? ['Invalid Email Address'] : []), [email, isValid]);

    const query = useMemo(() => QueryParametersParser.parse(search), [search]);
    const appName = useMemo(() => getAppNameByID(query?.app), [query]);
    const redirect = useMemo(() => query?.redirect, [query]);

    const onSubmit = useCallback(
        (e) => {
            e && e.preventDefault();
            setRequestError([]);
            setIsLoading(true);
            (async () => {
                try {
                    const request = await axios.post('/reset', {
                        email,
                        app: appName,
                        redirect,
                        query
                    });
                    if (request.status === 200) {
                        setIsSuccess(true);
                    }
                } catch (err) {
                    logService.error(err);
                    let title;
                    const messages = [];
                    if (
                        (err?.response?.status >= 400 && err?.response?.status < 500) ||
                        errorMessageMap(err?.response?.data?.error?.code)
                    ) {
                        title = 'Error';
                        messages.push(COMMON_ERROR_MESSAGE);
                    } else {
                        title = 'Error';
                        messages.push(NETWORK_ERROR_MESSAGE);
                    }

                    if (err?.response?.data?.error?.code === ERRORS_CODE.rateLimitError.code) {
                        const wait = moment.duration(err?.response?.data?.error?.lockTime).humanize();
                        messages.pop();
                        messages.push(
                            <span>
                                You have reached the maximum number (<b>{err?.response?.data?.error?.max}</b>) of reset
                                password request per day. Try again after <b>{wait}</b>.
                            </span>
                        );
                        messages.push('Please be sure to check spam folder on your mailbox.');
                    } else {
                        messages.push(errorMessageMap(err?.response?.data?.error?.code));
                    }

                    setRequestError([
                        {
                            title,
                            error: err,
                            messages
                        }
                    ]);
                }
                setIsLoading(false);
            })();
        },
        [email]
    );

    useEffect(() => {
        if (email && autoSubmit) {
            onSubmit();
        }
    }, [email, autoSubmit]);

    const resetPasswordForm = useMemo(() => {
        if (!isSuccess) {
            const onChange = (event) => {
                const emailValue = event.target.value;
                setEmail(emailValue);
                if (event.key === 'Enter' && emailValue && Validator.isEmail(emailValue)) {
                    onSubmit();
                }
            };

            return (
                <div className='reset-password__form'>
                    <h3>Reset Password</h3>
                    <h4>
                        Please enter your email address below. We will send you an email with detailed instructions to
                        securely reset your password.
                    </h4>
                    <CustomInputV2
                        required
                        label='Email'
                        name='email'
                        placeholder='Enter your email'
                        value={email}
                        errors={errors}
                        onChange={onChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && isValid) {
                                onSubmit(e);
                            }
                        }}
                        highlightErrors={!_isEmpty(requestError)}
                    />
                    {!_isEmpty(requestError) && <CustomSnackbarV2 messageList={requestError} />}
                </div>
            );
        }

        return (
            <div className='reset-password__form'>
                <h3>Please check your inbox.</h3>
                <h4>An email is on the way with instructions for resetting your password.</h4>
                <div className='reset-password__divider' />
                <h4>Didn't get the Password Reset email? Please check your Spam Folder.</h4>
            </div>
        );
    }, [isSuccess, isLoading, email, isValid, errors, appName, requestError, redirect, onSubmit]);

    return (
        <CommonLayoutV2 isLoading={false} pageTitle={pageTitle}>
            <div className='reset-password__container'>{resetPasswordForm}</div>
            <div className='reset-password__actions'>
                {!isSuccess && (
                    <CustomButtonV2 onClick={onSubmit} disabled={!isValid || isLoading} loading={isLoading}>
                        Submit
                    </CustomButtonV2>
                )}
                <Link to={`/login${location.search || ''}`}>Back to Sign In</Link>
            </div>
        </CommonLayoutV2>
    );
};

export default ResetPasswordPageV2;

ResetPasswordPageV2.propTypes = {
    location: PropTypes.object
};
